import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import { PiSmileySad } from 'react-icons/pi';

const formatAmount = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const numeric = Number(dateValue);
    const date =
      Number.isFinite(numeric) && numeric > 1e11
        ? new Date(numeric)
        : new Date(
            typeof dateValue === 'string' && dateValue.includes(' ')
              ? dateValue.replace(' ', 'T')
              : dateValue
          );
    if (Number.isNaN(date.getTime())) return dateValue;
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateValue;
  }
};

const renderAmount = (value) => {
  const formatted = formatAmount(value);
  if (!formatted) {
    return <span className={styles.pendingValue}>—</span>;
  }
  return formatted;
};

const PaymentLedgerTable = memo(
  ({
    data = [],
    isLoading = false,
    error = null,
    pagination = {},
    setSearchParams,
    page = 1,
    limit = 10,
  }) => {
    const transformedData = useMemo(
      () =>
        (data || []).map((item) => ({
          id: item.id,
          sessionTime: item.sessionTime || '—',
          cashierPoint: item.cashierPoint?.label || item.cashierPoint?.name || '—',
          userName: item.user?.name || '—',
          transactionType: item.transactionType || '—',
          amount: renderAmount(item.amount),
          balanceAfter: renderAmount(item.balanceAfter),
          remarks: item.remarks || '—',
          createdAt: formatDateTime(item.createdAt),
        })),
      [data]
    );

    const handleChangePage = useCallback(
      (event, newPage) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(newPage + 1));
          return next;
        });
      },
      [setSearchParams]
    );

    const handleChangeRowsPerPage = useCallback(
      (event) => {
        const newLimit = parseInt(event?.target?.value, 10) || 10;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(newLimit));
          next.set('page', '1');
          return next;
        });
      },
      [setSearchParams]
    );

    const columns = useMemo(
      () => [
        { id: 'sessionTime', label: 'Session Time', minWidth: 140 },
        { id: 'cashierPoint', label: 'Cashier Point', minWidth: 140 },
        { id: 'userName', label: 'User', minWidth: 120 },
        { id: 'transactionType', label: 'Transaction Type', minWidth: 150 },
        { id: 'amount', label: 'Amount', minWidth: 120, mono: true },
        { id: 'balanceAfter', label: 'Balance After', minWidth: 130, mono: true },
        { id: 'remarks', label: 'Remarks', minWidth: 160 },
        { id: 'createdAt', label: 'Transaction Time', minWidth: 160 },
      ],
      []
    );

    const totalCount = pagination?.totalRecords ?? 0;

    if (isLoading) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>Unable to load payment ledger</p>
            <p className={styles.emptyStateDesc}>{error}</p>
          </div>
        </div>
      );
    }

    if (!transformedData.length) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>No ledger records found</p>
            <p className={styles.emptyStateDesc}>
              Try adjusting the date range or payment method.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Table
          columns={columns}
          rows={transformedData}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={totalCount}
          disableInternalPagination
        />
      </div>
    );
  }
);

PaymentLedgerTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  pagination: PropTypes.object,
  setSearchParams: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
};

export default PaymentLedgerTable;
